import { FeatureSwitch, FeatureSwitchInfo, SystemFlowInfo } from '@if/core/types'
import { apiClient } from '@src/services/http-common'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/* Section API React query hooks */

const SCOPE_FEATURE_SWITCHES = 'feature-switches'
const SCOPE_SYSTEM_FLOW_SWITCHES = 'system-flow-switches'

const keysFactory = {
  featureSwitches: () => [SCOPE_FEATURE_SWITCHES] as const,
  systemFlowSwitches: () => [SCOPE_SYSTEM_FLOW_SWITCHES] as const,
}

const getAllFeatureSwitches = async () => {
  const response = await apiClient.get<FeatureSwitchInfo>('/FeatureSwitch')
  return response.data
}

export function useGetAllFeatureSwitches(): [FeatureSwitchInfo | undefined, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.featureSwitches(),
    queryFn: getAllFeatureSwitches,
  })

  return [data, isFetching]
}

const getSystemFlowSwitch = async () => {
  const response = await apiClient.get<SystemFlowInfo>('/SystemFlowSwitch')
  return response.data
}

export function useGetSystemFlowSwitch(): [SystemFlowInfo | undefined, boolean] {
    const {data, isFetching} = useQuery({
        queryKey: keysFactory.systemFlowSwitches(),
        queryFn: getSystemFlowSwitch,
    })
    return [data, isFetching]
}

// à discuter : est-ce qu'on veut modifier les FS à partir de LoanV2 aussi ?
const updateFeatureSwitch = async (id: string, data: FeatureSwitch) => {
  const response = await apiClient.put<FeatureSwitch>(`/FeatureSwitch/${id}`, data)
  return response.data
}

export function useUpdateFeatureSwitch(): [MutationFunction<FeatureSwitch, FeatureSwitch>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: FeatureSwitch) => updateFeatureSwitch(data.id, data),
    onSuccess: (updatedSwitch) => {
      const queryKey = keysFactory.featureSwitches()
      const currentData = queryClient.getQueryData<FeatureSwitchInfo>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FeatureSwitchInfo>(queryKey, {
          ...currentData,
          [updatedSwitch.id]: updatedSwitch,
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

// Export query functions for direct use
export { getAllFeatureSwitches }
